import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { PROJECTS } from "../data/site";
import { Lightbox } from "../components/Lightbox";

const CATS = ["All", "Residential", "Commercial", "Turnkey", "Modular", "Details"];

export default function WorkArchive() {
    const [filter, setFilter] = useState("All");
    const [lb, setLb] = useState({ project: null, index: null });

    const projects = useMemo(
        () => (filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter)),
        [filter]
    );

    const openLB = (project, index) => setLb({ project, index });
    const closeLB = () => setLb({ project: null, index: null });
    const prevLB = () =>
        setLb((s) => ({
            ...s,
            index: (s.index - 1 + s.project.gallery.length) % s.project.gallery.length,
        }));
    const nextLB = () =>
        setLb((s) => ({
            ...s,
            index: (s.index + 1) % s.project.gallery.length,
        }));

    return (
        <div className="min-h-screen bg-[#FAF9F6] text-[#1A1A1A] font-sans">
            {/* Simple header */}
            <header className="border-b border-[#1A1A1A]/10 bg-[#FAF9F6]/95 backdrop-blur-md sticky top-0 z-30">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between py-5">
                    <Link
                        to="/"
                        data-testid="work-back-home"
                        className="overline text-[#4A4643] hover:text-[#1A1A1A] transition-colors inline-flex items-center gap-2"
                    >
                        <ArrowLeft size={14} strokeWidth={1.6} />
                        Back home
                    </Link>
                    <div className="overline text-[#8C7C6D]">Selected work</div>
                    <Link
                        to="/#contact"
                        data-testid="work-contact-link"
                        className="overline text-[#1A1A1A] dd-link"
                    >
                        Contact
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-16 pb-14 lg:pt-24 lg:pb-20">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 lg:gap-12 items-end">
                    <div className="col-span-12 lg:col-span-7">
                        <div className="overline text-[#4A4643] mb-6">
                            Archive
                        </div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight text-[#1A1A1A]">
                            Selected <span className="italic text-[#8C7C6D]">projects</span>.
                        </h1>
                    </div>
                    <div className="col-span-12 lg:col-span-4 lg:col-start-9">
                        <p className="text-[#4A4643] text-base lg:text-lg leading-relaxed font-light">
                            A living archive of interiors we&apos;ve shaped across Pune, Goa
                            and beyond — new case studies added as they wrap.
                        </p>
                    </div>
                </div>
            </section>

            {/* Filter bar */}
            <div className="border-y border-[#1A1A1A]/10">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-4 flex flex-wrap items-center gap-2">
                    {CATS.map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            data-testid={`work-filter-${c.toLowerCase()}`}
                            className={`overline px-4 py-2 border transition-all duration-300 ${
                                filter === c
                                    ? "bg-[#1A1A1A] text-[#FAF9F6] border-[#1A1A1A]"
                                    : "border-transparent text-[#4A4643] hover:border-[#1A1A1A]/40 hover:text-[#1A1A1A]"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                    <div className="ml-auto overline text-[#4A4643]">
                        {String(projects.length).padStart(2, "0")}{" "}
                        {projects.length === 1 ? "project" : "projects"}
                    </div>
                </div>
            </div>

            {/* Projects list */}
            <main>
                {projects.length === 0 ? (
                    <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-32 text-center text-[#4A4643]">
                        <p className="font-serif italic text-2xl">
                            No projects in this category yet.
                        </p>
                    </div>
                ) : (
                    projects.map((p, i) => (
                        <ProjectSection
                            key={p.id}
                            project={p}
                            index={i}
                            onOpen={(idx) => openLB(p, idx)}
                        />
                    ))
                )}
            </main>

            {/* Footer CTA */}
            <section className="bg-[#1A1A1A] text-[#FAF9F6] py-20">
                <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 text-center">
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
                        Have a space in mind?
                    </h2>
                    <p className="mt-4 text-[#FAF9F6]/70 max-w-xl mx-auto font-light">
                        Every project on this page began with a conversation. Yours could be
                        next.
                    </p>
                    <Link
                        to="/#contact"
                        data-testid="work-cta-quote"
                        className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-[#FAF9F6] text-[#1A1A1A] text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#C7B299] transition-colors"
                    >
                        Start a conversation
                        <ArrowUpRight size={16} strokeWidth={1.5} />
                    </Link>
                </div>
            </section>

            {lb.project && (
                <Lightbox
                    images={lb.project.gallery}
                    index={lb.index}
                    onClose={closeLB}
                    onPrev={prevLB}
                    onNext={nextLB}
                />
            )}
        </div>
    );
}

const ProjectSection = ({ project, index, onOpen }) => {
    const gallery = project.gallery;
    // Alternate side-by-side hero on desktop for editorial pacing
    const isEven = index % 2 === 0;

    return (
        <section
            id={project.id}
            data-testid={`work-project-${project.id}`}
            className={`py-16 lg:py-24 ${index > 0 ? "border-t border-[#1A1A1A]/10" : ""}`}
        >
            <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="overline text-[#8C7C6D] mb-4">
                    {String(index + 1).padStart(2, "0")} — {project.category} · {project.year}
                </div>

                <div className="grid grid-cols-12 gap-6 lg:gap-12">
                    <div
                        className={`col-span-12 lg:col-span-4 ${
                            isEven ? "" : "lg:order-2 lg:col-start-9"
                        }`}
                    >
                        <h2 className="font-serif text-3xl lg:text-5xl text-[#1A1A1A] font-light tracking-tight leading-[1.05]">
                            {project.title}
                        </h2>
                        <div className="hairline my-6" />
                        <dl className="text-[15px] font-light leading-relaxed mb-6">
                            <MetaRow label="Location" value={project.location} />
                            <MetaRow label="Area" value={project.area} />
                            <MetaRow
                                label="Category"
                                value={project.category}
                            />
                        </dl>
                        <p className="text-[#4A4643] text-base leading-relaxed font-light">
                            {project.summary}
                        </p>
                    </div>

                    <motion.button
                        type="button"
                        onClick={() => onOpen(0)}
                        initial={{ opacity: 0, scale: 1.02 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9 }}
                        data-testid={`work-hero-${project.id}`}
                        className={`img-zoom col-span-12 lg:col-span-7 aspect-[4/3] lg:aspect-[16/10] bg-[#F0EFEA] overflow-hidden cursor-pointer ${
                            isEven ? "lg:col-start-6" : ""
                        }`}
                    >
                        <img
                            src={gallery[0].src}
                            alt={gallery[0].caption}
                            className="w-full h-full object-cover"
                        />
                    </motion.button>
                </div>

                {/* Small gallery strip */}
                <div className="mt-6 lg:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    {gallery.slice(1).map((img, i) => (
                        <motion.button
                            type="button"
                            key={img.src}
                            onClick={() => onOpen(i + 1)}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                            data-testid={`work-thumb-${project.id}-${i}`}
                            className="img-zoom aspect-[3/4] w-full bg-[#F0EFEA] overflow-hidden"
                        >
                            <img
                                src={img.src}
                                alt={img.caption}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </motion.button>
                    ))}
                </div>
            </div>
        </section>
    );
};

const MetaRow = ({ label, value }) => (
    <div className="flex items-baseline gap-6 py-1.5">
        <dt className="overline text-[#8C7C6D] w-24 flex-shrink-0">{label}</dt>
        <dd className="text-[#1A1A1A]">{value}</dd>
    </div>
);
